import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard, ShoppingBag, Users, Brain, CreditCard, MessageCircle, Trophy, TrendingUp, Bell, Shield, Bot } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import api, { clearAuth } from "../lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-nexus-4/artifacts/5uryz4az_ChatGPT%20Image%20Feb%208%2C%202026%2C%2003_09_14%20PM.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const stored = localStorage.getItem("vita_user");
    if (stored) setUser(JSON.parse(stored));
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error('Error:', err.message || err); }
    clearAuth();
    setUser(null);
    navigate("/");
  };

  const isHome = location.pathname === "/";

  const navLinks = user ? [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/vita-ai", label: "Vita AI", icon: Brain },
    { to: "/vita-coach", label: "Coach", icon: Bot },
    { to: "/community", label: "Community", icon: Users },
    { to: "/groups", label: "Groups", icon: Shield },
    { to: "/marketplace", label: "VitaMarket", icon: ShoppingBag },
    { to: "/messages", label: "Messages", icon: MessageCircle },
    { to: "/challenges", label: "Challenges", icon: Trophy },
    { to: "/progress", label: "Progress", icon: TrendingUp },
    { to: "/reminders", label: "Reminders", icon: Bell },
  ] : [
    { to: "/#features", label: "Features", icon: null },
    { to: "/#pricing", label: "Pricing", icon: null },
    { to: "/community", label: "Community", icon: Users },
    { to: "/marketplace", label: "VitaMarket", icon: ShoppingBag },
  ];

  return (
    <nav
      data-testid="main-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-[#2D6A4F]/10 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img src={LOGO_URL} alt="VittaNex" className="h-10 w-10 rounded-full object-cover transition-transform group-hover:scale-105" />
            <span className="font-['Outfit'] font-bold text-xl tracking-tight">
              <span className="text-[#0B6E4F]">Vitta</span>
              <span className="text-[#1B3A5C]">Nex</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-medium transition-colors hover:text-[#0B6E4F] ${
                  location.pathname === link.to ? "text-[#0B6E4F]" : "text-slate-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                {(!user.subscription_tier || user.subscription_tier === "free_trial") && (
                  <Link to="/pricing">
                    <Button data-testid="nav-subscribe-btn" className="bg-gradient-to-r from-[#0B6E4F] to-[#1B3A5C] hover:opacity-90 text-white rounded-full px-5 text-sm font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
                      <CreditCard className="mr-1.5 h-3.5 w-3.5" /> Subscribe
                    </Button>
                  </Link>
                )}
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-slate-50 transition-colors">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-[#0B6E4F] flex items-center justify-center text-white text-sm font-semibold">
                        {user.name?.[0]?.toUpperCase() || "U"}
                      </div>
                    )}
                    <span className="text-sm font-medium text-slate-700">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-pricing">
                    <CreditCard className="mr-2 h-4 w-4" /> Subscription
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <Shield className="mr-2 h-4 w-4" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/pricing">
                  <Button variant="ghost" data-testid="nav-pricing-btn" className="text-sm font-medium text-slate-600 hover:text-[#0B6E4F]">
                    <CreditCard className="mr-1.5 h-3.5 w-3.5" /> Subscribe
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="ghost" data-testid="nav-login-btn" className="text-sm font-medium text-slate-600 hover:text-[#0B6E4F]">
                    Log In
                  </Button>
                </Link>
                <Link to="/auth?mode=register">
                  <Button data-testid="nav-signup-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6 text-sm font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-6 animate-fade-in">
            <div className="flex flex-col gap-2 py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-3 rounded-xl text-sm font-medium text-slate-600 hover:bg-[#F2F9F6] hover:text-[#0B6E4F] transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <button onClick={handleLogout} className="px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 text-left transition-colors">
                  Logout
                </button>
              ) : (
                <Link to="/auth" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full mt-2">
                    Get Started
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
